import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from "framer-motion";

const services = [
  {
    title: "Business Support Services",
    description: "Streamline your daily garage operations—from admin tasks to team workflows—with expert support.",
    image: "/assets/Services/components/1.png",
    link: "/services/business-support"
  },
  {
    title: "Garage Management Software Setup",
    description: "Integrate robust digital systems for bookings, inventory, invoicing, and CRM—all in one dashboard.",
    image: "/assets/Services/components/2.png",
    link: "/services/garage-software-setup"
  },
  {
    title: "Digital Marketing",
    description: "Maximise your online presence with SEO, paid campaigns, and social content tailored for garages.",
    image: "/assets/Services/components/3.png",
    link: "/services/digital-marketing"
  },
  {
    title: "Parts Procurement",
    description: "Access trusted suppliers with fast sourcing of OEM and aftermarket parts—at competitive prices.",
    image: "/assets/Services/components/4.png",
    link: "/services/parts-procurement"
  },
  {
    title: "Lead Generation",
    description: "Fuel your garage’s growth with high-quality local leads from digital funnels built for conversion.",
    image: "/assets/Services/components/5.png",
    link: "/services/leads-generation"
  },
  {
    title: "Customer Experience Services",
    description: "Offer top-tier customer care with our white-labelled UK-based support trained for the auto sector.",
    image: "/assets/Services/components/6.png",
    link: "/services/customer-services"
  },
  {
    title: "Roadside Assistance",
    description: "Fast and reliable breakdown support to keep your customers safe and your reputation solid.",
    image: "/assets/Services/components/7.png",
    link: "/services/road-assistance"
  },
  {
    title: "Vehicle Recovery",
    description: "Professional towing and recovery services that reflect the reliability of your brand.",
    image: "/assets/Services/components/8.png",
    link: "/services/vehicle-recovery"
  },
  {
    title: "Vehicle Sales & Trade",
    description: "End-to-end assistance in buying and selling used vehicles with full documentation support.",
    image: "/assets/Services/components/9.png",
    link: "/services/sale-purchase"
  }
];

// Animation variants
const containerVariants = (reduced) =>
  reduced
    ? { hidden: { opacity: 1 }, show: { opacity: 1 } }
    : {
      hidden: { opacity: 0 },
      show: {
        opacity: 1,
        transition: { staggerChildren: 0.15, delayChildren: 0.1 },
      },
    };

const cardVariants = (reduced) =>
  reduced
    ? { hidden: { opacity: 1 }, show: { opacity: 1 } }
    : {
      hidden: { opacity: 0, y: 30 },
      show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
    };

const ServicesSection = ({ title = "Solutions Tailored for Your Automotive Business" }) => {
  const reduced = useReducedMotion();

  return (
    <section className="bg-white py-16 px-4 sm:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Section Title */}
        <motion.h2
          className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-center text-blue-900 mb-14 leading-snug"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
        >
          {title}
        </motion.h2>

        {/* Services Grid */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10"
          variants={containerVariants(reduced)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.05 }} // 👈 sensitive for mobile
        >
          {services.map((service) => (
            <motion.div key={service.link} variants={cardVariants(reduced)}>
              <Link
                to={service.link}
                className="group focus:outline-none focus:ring-2 focus:ring-blue-800 rounded-xl block h-full"
              >
                <div className="bg-white border border-blue-100 rounded-xl p-6 text-center shadow-md flex flex-col h-full transition-transform duration-300 group-hover:-translate-y-1 group-hover:shadow-xl">

                  {/* Image */}
                  <div className="relative overflow-hidden rounded-md">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-48 sm:h-56 object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>

                  {/* Title */}
                  <h3 className="mt-5 text-lg sm:text-xl font-semibold text-blue-900 group-hover:text-yellow-500 transition-colors duration-300">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="mt-3 text-gray-600 text-sm sm:text-base leading-relaxed flex-grow">
                    {service.description}
                  </p>

                  {/* CTA */}
                  <span className="mt-4 inline-block text-sm font-semibold text-blue-900/90 group-hover:text-blue-900">
                    Learn more →
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
